import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CelebrationHero = ({ userName, referenceNumber }) => {
  const [droplets, setDroplets] = useState([]);

  useEffect(() => {
    // Generate floating droplets
    const generateDroplets = () => {
      const newDroplets = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 8 + 4,
        delay: Math.random() * 2,
        duration: Math.random() * 3 + 2
      }));
      setDroplets(newDroplets);
    };

    generateDroplets();
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
      {/* Animated Background Droplets */}
      {droplets?.map((droplet) => (
        <motion.div
          key={droplet?.id}
          className="absolute rounded-full bg-gradient-to-br from-accent/30 to-primary/20 backdrop-blur-sm"
          style={{
            left: `${droplet?.x}%`,
            top: `${droplet?.y}%`,
            width: `${droplet?.size}px`,
            height: `${droplet?.size}px`,
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.7, 0.3]
          }}
          transition={{
            duration: droplet?.duration,
            delay: droplet?.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        {/* Animated Bottle */}
        <motion.div
          className="mb-8"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: "easeOutBack",
            delay: 0.3
          }}
        >
          <div className="relative">
            <motion.div
              className="w-32 h-48 bg-gradient-to-b from-accent via-primary to-secondary rounded-t-3xl rounded-b-lg mx-auto liquid-shadow"
              animate={{
                rotateY: [0, 15, -15, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* Bottle Cap */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-8 h-4 bg-secondary rounded-t-lg"></div>
              
              {/* Water Level Animation */}
              <motion.div
                className="absolute bottom-2 left-2 right-2 bg-gradient-to-t from-accent/80 to-accent/40 rounded-b-md"
                initial={{ height: "20%" }}
                animate={{ height: ["20%", "80%", "20%"] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
              
              {/* Mava Logo on Bottle */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <Icon name="Droplets" size={24} color="white" />
              </div>
            </motion.div>

            {/* Floating Success Icon */}
            <motion.div
              className="absolute -top-4 -right-4 w-12 h-12 bg-success rounded-full flex items-center justify-center liquid-shadow"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.5, duration: 0.5, ease: "easeOutBack" }}
            >
              <Icon name="Check" size={24} color="white" strokeWidth={3} />
            </motion.div>
          </div>
        </motion.div>

        {/* Thank You Message */}
        <motion.div
          className="text-center max-w-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-6xl font-poppins font-bold text-primary mb-4">
            Thank You{userName ? `, ${userName}` : ''}!
          </h1>
          
          <motion.div
            className="inline-block"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <p className="text-xl md:text-2xl font-poppins font-semibold text-secondary mb-6">
              Stay Cool. Stay Mava.
            </p>
          </motion.div>

          <p className="text-lg text-text-primary mb-8 leading-relaxed">
            Your enquiry has been successfully submitted! Our hydration experts will connect with you soon to discuss how Mava can elevate your daily hydration experience.
          </p>

          {/* Reference Number */}
          {referenceNumber && (
            <motion.div
              className="bg-card border border-border rounded-lg p-6 mb-8 liquid-shadow"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
            >
              <p className="text-sm text-text-secondary mb-2">Your Reference Number</p>
              <p className="text-2xl font-poppins font-bold text-primary tracking-wider">
                {referenceNumber}
              </p>
              <p className="text-sm text-text-secondary mt-2">
                Keep this for your records
              </p>
            </motion.div>
          )}

          {/* Response Timeline */}
          <motion.div
            className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg p-6 mb-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-3">
              <Icon name="Clock" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-poppins font-semibold text-primary">
                What's Next?
              </h3>
            </div>
            <p className="text-text-primary">
              Our team will review your enquiry and respond within <strong>24-48 hours</strong> with personalized recommendations and pricing details.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default CelebrationHero;